import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { supabase } from "../../../../supabase";
import styles from "./Preview.module.css";
import { Helmet } from "react-helmet";
import IconButton from "../../../../components/admin/IconButton";
import { ICON_PATHS } from "../../../../constants/iconPaths";

function PreviewPage() {
  const { id } = useParams(); // URL의 ID 파라미터
  const [searchParams] = useSearchParams(); // 쿼리 파라미터
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackGiven, setFeedbackGiven] = useState(false); // 피드백 상태
  const [typedCommentary, setTypedCommentary] = useState(""); // 타이핑된 코멘트
  const [aiCommentTitleImage, setAiCommentTitleImage] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);


  // 쿼리 파라미터에서 team 값 가져오기
  const teamParam = searchParams.get("team"); // team1, team2, team3

  useEffect(() => {
    const handleScroll = () => {
      // 전체 스크롤 가능한 높이
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      // 현재 스크롤 위치
      const currentScroll = window.scrollY;
      // 스크롤 위치가 50% 이상일 때 표시
      setShowFeedback(currentScroll > scrollHeight * 0.5);
    };
  
    window.addEventListener('scroll', handleScroll);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  function useAnimatedNumber(targetNumber = 0, duration = 1000, delay = 0) {
    const [animatedNumber, setAnimatedNumber] = useState(0);
  
    useEffect(() => {
      if (targetNumber === null || targetNumber === undefined || targetNumber <= 0) {
        setAnimatedNumber(0);
        return;
      }
  
      const startAnimation = () => {
        const startTime = Date.now();
        const easeOut = (t) => 1 - Math.pow(1 - t, 3); // Ease-out cubic function
  
        const interval = setInterval(() => {
          const elapsedTime = Date.now() - startTime;
          const progress = Math.min(elapsedTime / duration, 1); // Progress ratio (0 ~ 1)
          const easedProgress = easeOut(progress);
  
          setAnimatedNumber(Math.round(targetNumber * easedProgress));
  
          if (progress >= 1) {
            clearInterval(interval);
          }
        }, 16); // ~60fps for smooth animation
      };
  
      const timeout = setTimeout(() => {
        startAnimation();
      }, delay);
  
      return () => {
        clearTimeout(timeout);
      };
    }, [targetNumber, duration, delay]);
  
    return animatedNumber;
  }
  const animatedTeam1Rate = useAnimatedNumber(previewData?.team1_trophy_avg || 0, 1000, 1000);
const animatedTeam2Rate = useAnimatedNumber(previewData?.team2_trophy_avg || 0, 1000, 1000);
const animatedTeam3Rate = useAnimatedNumber(previewData?.team3_trophy_avg || 0, 1000, 1000);
  
  useEffect(() => {
    const fetchAiCommentImage = async () => {
      const { data, error } = await supabase.storage
        .from("asset")
        .getPublicUrl("/ptl/logo_ai_emoji.svg"); // 이미지 파일 경로
      if (error) {
        console.error("Error fetching AI comment title image:", error);
      } else {
        setAiCommentTitleImage(data.publicUrl);
      }
    };

    fetchAiCommentImage();
  }, []);

  useEffect(() => {
    const fetchPreview = async () => {
        const startTime = Date.now(); // 로딩 시작 시간
        try {
          const { data, error } = await supabase
            .from("ptl_preview")
            .select(`
              id, date, time, stadium, commentary_a, commentary_b, commentary_c,
              team1_trophy_avg, team2_trophy_avg, team3_trophy_avg, team1_wdl, team2_wdl, team3_wdl,
              vs1_home_avg, vs1_away_avg, vs2_home_avg, vs2_away_avg, vs3_home_avg, vs3_away_avg,
              team1 ( id, name, level, age, win_avg, goal_avg, goal_against_avg, win_draw_lose ),
              team2 ( id, name, level, age, win_avg, goal_avg, goal_against_avg, win_draw_lose  ),
              team3 ( id, name, level, age, win_avg, goal_avg, goal_against_avg, win_draw_lose )
            `)
            .eq("id", id)
            .single();
      
          if (error) {
            console.error("Error fetching preview:", error);
            setPreviewData(null);
          } else {
            setPreviewData({
              ...data,
              team3: data.team3 || { id: null, name: "N/A", level: "-", age: "-", win_avg: "-", goal_avg: "-", goal_against_avg: "-" }, // 기본값 제공
            });
          }
        } catch (err) {
          console.error("Unexpected error:", err);
          setPreviewData(null);
        } finally {
          const elapsedTime = Date.now() - startTime;
          const delay = Math.max(1000 - elapsedTime, 0);
          setTimeout(() => setLoading(false), delay);
        }
      };

    fetchPreview();
    console.log("Current teamParam:", teamParam); // 쿼리 파라미터 값 출력
  }, [id, teamParam]);
  

  useEffect(() => {
    if (previewData) {
      let commentary = "";
      if (teamParam === "team1") commentary = previewData.commentary_a || "";
      else if (teamParam === "team2") commentary = previewData.commentary_b || "";
      else if (teamParam === "team3") commentary = previewData.commentary_c || "";
  
      if (!commentary) {
        setTypedCommentary("코멘터리가 없습니다.");
      } else {
        setTypedCommentary(commentary);
      }
    }
  }, [previewData, teamParam]);

  const handleFeedback = async (isLiked) => {
    if (!previewData) return;

    // team_id 결정 로직
    let teamId = null;
    if (teamParam === "team1") {
      teamId = previewData.team1.id;
    } else if (teamParam === "team2") {
      teamId = previewData.team2.id;
    } else if (teamParam === "team3") {
      teamId = previewData.team3.id;
    }

    if (!teamId) {
      alert("올바르지 않은 팀 선택입니다.");
      return;
    }

    try {
      const { error } = await supabase.from("ptl_preview_feedback").insert([
        {
          ptl_preview_id: id,
          is_liked: isLiked,
          team_id: teamId,
        },
      ]);

      if (error) {
        console.error("Error submitting feedback:", error);
        alert("피드백 제출에 실패했습니다. 다시 시도해주세요.");
      } else {
        setFeedbackGiven(true); // 피드백 상태 업데이트
        // 성공적인 피드백 제출 시에는 얼럿을 제거
      }
    } catch (err) {
      console.error("Unexpected error submitting feedback:", err);
      alert("알 수 없는 오류가 발생했습니다. 다시 시도해주세요.");
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingLogo}></div>
      </div>
    );
  }

  if (!previewData) {
    return <div className={styles.error}>Preview not found</div>;
  }

  const {
    date,
    time,
    stadium,
    commentary_a,
    commentary_b,
    commentary_c,
    team1_trophy_avg,
    team2_trophy_avg,
    team3_trophy_avg,
    team1_wdl,
    team2_wdl,
    team3_wdl,
    team1,
    team2,
    team3,
  } = previewData;

  // 팀별 코멘터리 결정
  const commentary =
    teamParam === "team1"
      ? commentary_a
      : teamParam === "team2"
      ? commentary_b
      : teamParam === "team3"
      ? commentary_c
      : "팀에 대한 코멘터리가 없습니다.";

  const matchPredictions = [
    { match: `${team1.name} vs ${team2.name}`, winRate: team1.win_avg },
    { match: `${team2.name} vs ${team3.name}`, winRate: team2.win_avg },
    { match: `${team3.name} vs ${team1.name}`, winRate: team3.win_avg },
  ];

  return (
    <>
    <Helmet>
      <title>Plab AI - 프리뷰</title>
      <meta property="og:title" content="Plab AI - 프리뷰" />
      <meta property="og:description" content="프리뷰 페이지" />
      <meta property="og:type" content="website" />
      <meta name="twitter:title" content="Plab AI - 프리뷰" />
      <meta name="twitter:description" content="프리뷰 페이지" />
    </Helmet>
    <div className={styles.container}>
        <div className={styles.sectionContainer}>
          <div className={styles.logoContainer}>
            <img src="/asset/logo_plab_ai.svg" alt="OG_plab24"></img>
          </div>
      
      <section className={styles.section}>
        <div className={styles.sectionHeader}><h3>우승 예측</h3></div>
        <div className={styles.sectionBody}>
        <div className={styles.trophyPrediction}>
  <div>
    <p className={styles.trophyPredictionRate}>{animatedTeam1Rate}%</p>
    <p className={styles.trophyPredictionWDL}>{team1_wdl}</p>
    <p className={styles.trophyPredictionTeam}>{team1.name}</p>
  </div>
  <div>
    <p className={styles.trophyPredictionRate}>{animatedTeam2Rate}%</p>
    <p className={styles.trophyPredictionWDL}>{team2_wdl}</p>
    <p className={styles.trophyPredictionTeam}>{team2.name}</p>
  </div>
  {team3.id && (
    <div>
      <p className={styles.trophyPredictionRate}>{animatedTeam3Rate}%</p>
      <p className={styles.trophyPredictionWDL}>{team3_wdl}</p>
      <p className={styles.trophyPredictionTeam}>{team3.name}</p>
    </div>
  )}
</div>
        </div>
      </section>
<section className={styles.section}>
  <div className={styles.sectionHeader}>
    <h3>팀 데이터 비교</h3>
  </div>
  <div className={styles.sectionBody}>
    <div className={styles.teamComparisonContainer}>
      {/* 팀 이름 */}
      <div className={styles.teamRow}>
        <div className={styles.teamIndex}></div>
        {[team1, team2, team3].map((team) => (
          <div key={team.id || "team-placeholder"} className={styles.teamName}>
            {team.name}
          </div>
        ))}
      </div>

      {/* 데이터 행 */}
      <div className={styles.teamRow}>
        <div className={styles.teamIndex}>레벨</div>
        {[team1, team2, team3].map((team) => (
          <div key={team.id || "level-placeholder"} className={styles.teamData}>
            {team.level || "-"}
          </div>
        ))}
      </div>
      <div className={styles.teamRow}>
        <div className={styles.teamIndex}>나이</div>
        {[team1, team2, team3].map((team) => (
          <div key={team.id || "age-placeholder"} className={styles.teamData}>
            {team.age || "-"}
          </div>
        ))}
      </div>
      <div className={styles.teamRow}>
        <div className={styles.teamIndex}>전적</div>
        {[team1, team2, team3].map((team) => (
          <div key={team.id || "win_draw_lose-placeholder"} className={styles.teamData}>
            {team.win_draw_lose || "-"}
          </div>
        ))}
      </div>
      <div className={styles.teamRow}>
  <div className={styles.teamIndex}>승률</div>
  {[team1, team2, team3].map((team) => (
    <div key={team.id || "win-avg-placeholder"} className={styles.teamData}>
      {team.win_avg !== null && team.win_avg !== undefined ? `${team.win_avg}%` : "-"}
    </div>
  ))}
</div>
      <div className={styles.teamRow}>
        <div className={styles.teamIndex}>평균 득점</div>
        {[team1, team2, team3].map((team) => (
          <div key={team.id || "goal-avg-placeholder"} className={styles.teamData}>
            {team.goal_avg || "-"}
          </div>
        ))}
      </div>
      <div className={styles.teamRow}>
        <div className={styles.teamIndex}>평균 실점</div>
        {[team1, team2, team3].map((team) => (
          <div key={team.id || "goal-against-placeholder"} className={styles.teamData}>
            {team.goal_against_avg || "-"}
          </div>
        ))}
      </div>
    </div>
  </div>
</section>
    <section className={styles.section}>
  <div className={styles.sectionHeader}>
    <h3>승리 확률</h3>
  </div>
  <div className={styles.sectionBody}>
    <div className={styles.victoryMatches}>
    {[
  {
    homeTeam: team1.name,
    awayTeam: team2.name,
    homeRate: previewData.vs1_home_avg,
    awayRate: previewData.vs1_away_avg,
    homeColor: "var(--blue-500)",
    awayColor: "var(--red-500)",
  },
  team3.id && {
    homeTeam: team2.name,
    awayTeam: team3.name,
    homeRate: previewData.vs2_home_avg,
    awayRate: previewData.vs2_away_avg,
    homeColor: "var(--red-500)",
    awayColor: "var(--yellow-500)",
  },
  team3.id && {
    homeTeam: team3.name,
    awayTeam: team1.name,
    homeRate: previewData.vs3_home_avg,
    awayRate: previewData.vs3_away_avg,
    homeColor: "var(--yellow-500)",
    awayColor: "var(--blue-500)",
  },
]
  .filter(Boolean) // team3 관련 데이터를 제외
  .map((match, index) => {
    const drawRate = 100 - match.homeRate - match.awayRate;

    return (
      <div key={index} className={styles.victoryContainer}>
        {/* 그래프 */}
        <div className={styles.victoryGraph}>
          <div
            className={styles.victoryBarHome}
            style={{
              backgroundColor: match.homeColor,
              width: `${match.homeRate}%`,
            }}
          >
            <span className={styles.victoryLabel}>{match.homeRate}%</span>
          </div>
          <div
            className={styles.victoryBar}
            style={{
              backgroundColor: "var(--gray-700)",
              width: `${drawRate}%`,
            }}
          ></div>
          <div
            className={styles.victoryBarAway}
            style={{
              backgroundColor: match.awayColor,
              width: `${match.awayRate}%`,
            }}
          >
            <span className={styles.victoryLabel}>{match.awayRate}%</span>
          </div>
        </div>
        {/* 팀 라벨 */}
        <div className={styles.teamLabels}>
          <span className={styles.teamLabel}>{match.homeTeam}</span>
          <span className={styles.teamLabel}>{match.awayTeam}</span>
        </div>
      </div>
    );
  })}
    </div>
  </div>
</section>
        <section className={styles.section}>
        <div className={styles.sectionHeader}>
              {aiCommentTitleImage ? (
                <img src={aiCommentTitleImage} alt="AI 코멘트 타이틀" className={styles.titleImage} />
              ) : (
                <h3>AI 코멘트</h3> // 이미지가 로드되지 않았을 때 기본 텍스트
              )}
            </div>
          <div className={styles.sectionBody}>
            <p className={styles.commentary}>
                {typedCommentary.split("\n").map((line, index) => (
                <React.Fragment key={index}>
                    {line}
                    <br />
                </React.Fragment>
                ))}
            </p>
          </div>
        </section>
        {showFeedback && (
        <div className={styles.floatingFeedback}>
            {!feedbackGiven ? (
            <div className={styles.feedbackButtons}>
              <span>이 콘텐츠가 재밌으셨나요?</span>
                <IconButton
                icon={ICON_PATHS.like}
                onClick={() => handleFeedback(true)}
                />
                <IconButton
                icon={ICON_PATHS.dislike}
                onClick={() => handleFeedback(false)}
                />
            </div>
            ) : (
            <p className={styles.thankYouMessage}><a href="https://bit.ly/plabAI_survey">추가 의견 남기기</a></p>
            )}
          </div>
        )}
        </div>
    </div>
    </>
  );
}

export default PreviewPage;